/**
 * Catálogo dos 9 TARPs v5.10 + 3 do C-88 (INST-AT, INST-AL, TILT-EMG — ADR-0038)
 * + fluxos ramificados + gatilho→TARP (GUPM-171).
 * -------------------------------------------------------------------------
 * Porta `const TARPS` + os flow builders do protótipo v5.10, usando os tipos
 * do motor (GUPM-170).
 *
 * ⚠️ SEM PII: o protótipo tinha telefones e um nome pessoal hardcoded — aqui
 * o texto refere PAPÉIS ("RT Foscentro", "Defesa Civil"), `wa.to` é sempre ''
 * e os contatos reais vêm do banco (tabela `contacts` via contatos datasource).
 * Ver security.md / frontend.md / [[project_gupm158_contatos_pii_cloud]].
 *
 * TS puro: sem React, sem Supabase, sem DOM.
 */
import type { InstrumentType, TarpLevel } from '../types';
import { INSTRUMENT_TYPE_META } from '../instrumentos/instrument-type';
import { type Flow, type FlowNode, orderedNodeIds } from './flow';

export type TarpCode =
  | 'TARP-PZ-AT'
  | 'TARP-PZ-AL'
  | 'TARP-MS-AL'
  | 'TARP-VIS-AL'
  | 'TARP-EMG'
  | 'TARP-PS-INST'
  | 'TARP-PS-CAM'
  | 'TARP-PS-TILT'
  | 'TARP-INST-EMG'
  // C-88 (ADR-0038) — não estão no protótipo v5.10: o código nomeia o PROTOCOLO, e o
  // histórico não pode gravar "PZ" num tiltímetro. O tipo vem do instrumento da TARP.
  | 'TARP-INST-AT'
  | 'TARP-INST-AL'
  | 'TARP-TILT-EMG';

export interface WhatsAppStep {
  type: 'whatsapp';
  /** Sempre '' — preenchido do banco (contacts). */
  to: string;
  msg: string;
  label: string;
}

export type Step = string | WhatsAppStep;

export interface TarpCatalogEntry {
  code: TarpCode;
  nivel: TarpLevel;
  title: string;
  subtitle: string;
  warning?: string;
  requerDiagnostico?: boolean;
  perdaSinal?: boolean;
  steps: Step[];
  flow?: Flow;
}

// ── helpers ───────────────────────────────────────────────────
function withIds(nodes: Record<string, Omit<FlowNode, 'id'>>): Record<string, FlowNode> {
  const out: Record<string, FlowNode> = {};
  for (const [k, n] of Object.entries(nodes)) out[k] = { ...n, id: k };
  return out;
}

// ── Sub-fluxo de emergência reutilizável (GUPM-207) ───────────
// As perguntas condicionais de emergência (AAS → confirmar automático / acionar
// manual → comunicar RT + Coordenador PAEBM) EMBUTIDAS antes de todo terminal
// `paebm`, em vez de encaminhar a uma segunda TARP (TARP-EMG). Nós prefixados
// `emg_*` para não colidir com os ids do fluxo hospedeiro; o passo final aponta
// para o terminal `paebm` do próprio fluxo (`terminalId`). SEM PII.
function emergencySubflow(terminalId: string): {
  entry: string;
  nodes: Record<string, Omit<FlowNode, 'id'>>;
  order: string[];
} {
  const aas = 'emg_aas';
  const auto = 'emg_auto';
  const manual = 'emg_manual';
  const comunica = 'emg_comunica';
  return {
    entry: aas,
    nodes: {
      [aas]: {
        tipo: 'decisao',
        texto: 'As sirenes dispararam AUTOMATICAMENTE pelos tiltímetros (AAS)?',
        sim: auto,
        nao: manual,
        simNote: 'Confirme o disparo e o cenário emitido no SNM.',
        naoNote: 'Acione MANUALMENTE as sirenes agora — não espere.',
      },
      [auto]: {
        tipo: 'acao',
        texto:
          'Confirme o disparo automático das sirenes e o cenário/mensagem emitidos no Sistema de ' +
          'Notificação em Massa (SNM). Verifique nas câmeras a confirmação visual da ocorrência e ' +
          'registre o horário.',
        next: comunica,
      },
      [manual]: {
        tipo: 'acao',
        texto:
          'Acione MANUALMENTE as sirenes (UPM-01 a UPM-06) pela interface do software — aba SNM. ' +
          'Selecione Ativação Total ou por Grupos conforme a região da ZAS afetada. Registre o ' +
          'horário exato do acionamento.',
        next: comunica,
      },
      [comunica]: {
        tipo: 'acao',
        // Texto do protótipo sem os nomes do Coordenador e do substituto: as pessoas vêm do
        // cadastro de Contatos de Emergência, nunca do código (security.md).
        texto:
          'Comunique IMEDIATAMENTE o RT Foscentro e solicite o acionamento do Coordenador do PAEBM ' +
          'e de seu substituto. Informe: sirenes acionadas, horário, e descrição visual da ' +
          'ocorrência. Use todos os meios disponíveis: celular, WhatsApp, rádio.\n\n' +
          'A partir deste ponto, o Coordenador do PAEBM assume o protocolo: notificação da ANM, ' +
          'Defesa Civil, Prefeitura(s), Corpo de Bombeiros, evacuação da ZAS e preenchimento da ' +
          'DIE no SIGBM. O vigilante mantém comunicação aberta e acompanha pelas câmeras.',
        wa: {
          to: '',
          msg: '[GeoGuard UPM] EMERGÊNCIA NE-3 — Ruptura em curso. Sirenes acionadas. Solicito acionamento imediato do Coordenador do PAEBM. Aguardo orientação.',
          label: 'WhatsApp RT Foscentro',
        },
        next: terminalId,
      },
    },
    order: [aas, auto, manual, comunica],
  };
}

// Rótulos amigáveis dos passos do sub-fluxo de emergência: o badge de etapa mostra
// `labels[id] ?? id`; sem isto, o id interno `emg_*` vazaria na UI numa emergência.
const EMERGENCY_LABELS: Record<string, string> = {
  emg_aas: 'E1',
  emg_auto: 'E2',
  emg_manual: 'E3',
  emg_comunica: 'E4',
};

// Texto único do terminal `paebm` após o sub-fluxo embutido: acionamento feito,
// encaminha ao módulo PAEBM (o Coordenador conduz o restante). SEM PII.
const PAEBM_TERMINAL_TEXTO =
  'Acionamento de emergência registrado. Vá ao PAEBM: o Coordenador conduz a DIE/SIGBM, as ' +
  'notificações externas (ANM, Defesa Civil, Bombeiros), a evacuação da ZAS (NE-3, Res. 95/2022) e a ' +
  'declaração formal de Emergência (Res. 220/2025). Mantenha comunicação aberta e acompanhe pelas câmeras.';

// Embute o sub-fluxo de emergência antes do terminal `paebm` (id `terminalId`) de
// um fluxo hospedeiro (GUPM-207): reaponta as arestas que iam direto ao terminal
// para a entrada do sub-fluxo, injeta os nós `emg_*` e insere-os na ordem logo
// antes do terminal. O terminal permanece `paebm` (texto unificado).
function spliceEmergency(flow: Flow, terminalId: string): Flow {
  const sub = emergencySubflow(terminalId);
  const nodes: Record<string, FlowNode> = {};
  // 1) Reaponta arestas dos nós existentes que iam direto ao terminal → entrada do sub-fluxo.
  for (const [id, n] of Object.entries(flow.nodes)) {
    nodes[id] = {
      ...n,
      ...(n.sim === terminalId ? { sim: sub.entry } : {}),
      ...(n.nao === terminalId ? { nao: sub.entry } : {}),
      ...(n.next === terminalId ? { next: sub.entry } : {}),
    };
  }
  // 2) Injeta os nós do sub-fluxo (o `comunica` aponta ao terminal preservado).
  for (const [k, v] of Object.entries(sub.nodes)) nodes[k] = { ...v, id: k };
  // 3) Terminal `paebm` com o texto unificado de encaminhamento ao PAEBM.
  const term = nodes[terminalId];
  if (term) nodes[terminalId] = { ...term, tipo: 'paebm', paebm: true, texto: PAEBM_TERMINAL_TEXTO };
  // 4) Ordem: insere os nós do sub-fluxo imediatamente antes do terminal.
  const order = [...(flow.order ?? orderedNodeIds(flow))];
  const ti = order.indexOf(terminalId);
  order.splice(ti >= 0 ? ti : order.length, 0, ...sub.order);
  return { start: flow.start, nodes, order, labels: { ...(flow.labels ?? {}), ...EMERGENCY_LABELS } };
}

// ── Fluxo ramificado de instrumentação (buildInstFlow) ────────
function buildInstFlow(nivel: TarpLevel): Flow {
  const emerg = nivel === 'EMERGENCIA';
  const prazoLeitura = emerg
    ? 'até 1 hora'
    : nivel === 'ALERTA'
      ? 'até 2 horas'
      : 'até 12 horas (ciclo normal)';
  const prazoDiag = emerg ? '12 horas' : nivel === 'ALERTA' ? '24 horas' : '48 horas';
  const nivelTxt = emerg ? 'Emergência' : nivel === 'ALERTA' ? 'Alerta' : 'Atenção';

  const nodes: Record<string, Omit<FlowNode, 'id'>> = {
    start: {
      tipo: 'inicio',
      texto:
        `Instrumento em ${nivelTxt} (conforme Carta de Risco). ` +
        (emerg
          ? 'Um e-mail padrão foi enviado às partes (EoR, RT, JF Brasil, TetraTech) e o RT comunicado por celular/WhatsApp.'
          : 'Um e-mail padrão de comunicação foi enviado às partes (EoR, RT, JF Brasil, TetraTech).'),
      next: '1',
    },
    '1': { tipo: 'decisao', texto: 'Você viu algo anormal nas câmeras?', sim: '8', nao: '2' },
    '2': {
      tipo: 'acao',
      texto:
        'Solicite a repetição da leitura do instrumento e acompanhe a situação pelas câmeras. ' +
        `Prazo para a releitura: ${prazoLeitura}.`,
      next: '3',
    },
    '3': {
      tipo: 'decisao',
      texto: 'A leitura voltou ao normal?',
      sim: '6a',
      nao: emerg ? '9' : '4',
      ...(emerg ? { naoNote: 'Em emergência, siga também para a cadeia de escalonamento (contato com o RT).' } : {}),
    },
    '4': {
      tipo: 'diagnostico',
      texto:
        'Comunique o RT e solicite o diagnóstico técnico. Acompanhe a situação pelas câmeras até o ' +
        `diagnóstico ser concluído. Prazo do RT para avaliar: ${prazoDiag}.`,
      // GUPM-184: enquadramento ANM é decisão do RT NA BAIXA (não no checklist de
      // abertura) → o diagnóstico segue direto para a baixa da TARP.
      next: '6a',
    },
    '6a': {
      tipo: 'baixa',
      texto:
        'Encaminhe para a baixa da TARP. O preenchimento do formulário de baixa — incluindo o ' +
        'enquadramento em nível ANM e o plano de ação, quando aplicável — é realizado pelo técnico no ' +
        'próprio item de baixa da TARP.',
      handoff: true,
    },
    '8': { tipo: 'decisao', texto: 'É uma situação de risco (possível rompimento)?', sim: '9', nao: '19' },
    '9': {
      tipo: 'acao',
      texto: 'Contate o RT (celular e WhatsApp) e aguarde orientação. Prepare-se para as ações.',
      wa: {
        to: '',
        msg: `[GeoGuard UPM] Instrumento em ${emerg ? 'EMERGÊNCIA' : nivel}. Possível situação de risco. Aguardando orientação do RT.`,
        label: 'WhatsApp RT Foscentro',
      },
      next: '10',
    },
    '10': { tipo: 'decisao', texto: 'O RT respondeu?', sim: '15', nao: '11' },
    '11': {
      tipo: 'acao',
      texto: 'Contate o contato secundário da cadeia de escalonamento (celular e WhatsApp).',
      next: '12',
    },
    '12': { tipo: 'decisao', texto: 'O contato secundário respondeu?', sim: '15', nao: '13' },
    '13': {
      tipo: 'acao',
      texto: 'Contate o contato terciário da cadeia de escalonamento (celular e WhatsApp).',
      next: '14',
    },
    '14': { tipo: 'decisao', texto: 'O contato terciário respondeu?', sim: '15', nao: '16' },
    '15': {
      tipo: 'decisao',
      texto: 'Após contato com o responsável: é uma situação de ruptura?',
      sim: '20',
      nao: '6a',
      naoNote: 'Sem ruptura, o técnico segue para a baixa da TARP (ou enquadramento de nível, se for o caso).',
    },
    '16': {
      tipo: 'acao',
      texto: 'Nenhum contato da cadeia respondeu. Aguarde e acompanhe as câmeras e os demais instrumentos.',
      next: '17',
    },
    '17': { tipo: 'decisao', texto: 'A situação está progredindo?', sim: '18', nao: '16' },
    '18': {
      tipo: 'decisao',
      texto: 'A ruptura está prestes a acontecer ou já está acontecendo (caso induvidável)?',
      sim: '20',
      nao: '9',
    },
    '19': {
      tipo: 'decisao',
      texto: 'A situação coloca em risco pessoas, equipamento ou operação?',
      sim: '4',
      nao: '19n',
      simNote: 'Ligue para o RT e isole a área (segue para o diagnóstico técnico).',
    },
    '19n': { tipo: 'acao', texto: 'Reporte ao setor responsável e direcione para a baixa da TARP.', next: '6a' },
    '20': {
      tipo: 'paebm',
      texto:
        'Acione as sirenes e comunique o RT para o acionamento do Protocolo PAEBM ' +
        '(cadeia de escalonamento e ramo de emergência).',
      paebm: true,
    },
  };
  // GUPM-207: embute o sub-fluxo de emergência antes do terminal `paebm` ('20').
  return spliceEmergency(
    {
      start: 'start',
      nodes: withIds(nodes),
      // Ordem canônica das etapas 0..20 (fiel ao FLOW_ORDER do protótipo).
      order: ['start', '1', '2', '3', '4', '6a', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '19n', '20'],
      labels: { start: '0', '6a': '6', '19n': '19' },
    },
    '20',
  );
}

// Perda de sinal de instrumento geotécnico — reaproveita instFlow(ATENCAO).
function buildSignalLossFlow(): Flow {
  const flow = buildInstFlow('ATENCAO');
  const n = flow.nodes;
  n['start']!.texto =
    'Um instrumento geotécnico parou de enviar leituras (perda de sinal — não é leitura de nível). ' +
    'Um e-mail padrão de comunicação foi enviado às partes (EoR, RT, JF Brasil, TetraTech).';
  n['2']!.texto =
    'Solicite a verificação da integridade do instrumento (e seus acessórios) à TetraTech (manutenção) e ' +
    'acompanhe a situação pelas câmeras. Prazo: até 48 horas.';
  n['2']!.wa = {
    to: '',
    msg: '[GeoGuard UPM] Perda de sinal de instrumento. Solicito verificação de integridade (TetraTech).',
    label: 'WhatsApp TetraTech',
  };
  n['3']!.texto = 'Foi constatada avaria no equipamento (e seus acessórios)?';
  n['3']!.sim = '6a';
  n['3']!.nao = '4';
  n['3']!.simNote = 'Com avaria constatada, o técnico direciona para a baixa da TARP.';
  n['3']!.naoNote = 'Sem avaria, comunique o RT e solicite o diagnóstico técnico.';
  return flow;
}

// Protocolo PAEBM / Ruptura em curso (TARP-EMG). É o próprio sub-fluxo de emergência
// (GUPM-207): início (ruptura NE-3) → AAS → confirmar automático / acionar manual →
// comunicar RT+Coordenador → terminal `paebm` (encaminha ao módulo PAEBM). O MESMO
// sub-fluxo é embutido nos demais ramos `paebm` dos fluxos-gatilho. SEM PII (papéis,
// `wa.to=''`). Terminal `paebm` (era `fim` no GUPM-190 T4) — emergência vai ao PAEBM.
function buildEmgFlow(): Flow {
  const terminalId = 'fim_paebm';
  const sub = emergencySubflow(terminalId);
  const nodes: Record<string, Omit<FlowNode, 'id'>> = {
    start: {
      tipo: 'inicio',
      texto:
        'SITUAÇÃO DE RUPTURA EM CURSO — Nível NE-3 declarado automaticamente (Art. 32, Res. ANM 95/2022) ' +
        'e Emergência pela Res. 220/2025. NÃO aguarde instrução de nível: sirenes devem ser acionadas ' +
        'IMEDIATAMENTE. A declaração formal e as notificações externas (ANM, Defesa Civil, Bombeiros) são ' +
        'responsabilidade do Coordenador do PAEBM.',
      next: sub.entry,
    },
    ...sub.nodes,
    [terminalId]: { tipo: 'paebm', paebm: true, texto: PAEBM_TERMINAL_TEXTO },
  };
  return {
    start: 'start',
    nodes: withIds(nodes),
    order: ['start', ...sub.order, terminalId],
    labels: { ...EMERGENCY_LABELS, [terminalId]: 'E5' },
  };
}

// Perda de sinal das câmeras (regra: câmeras + tiltímetros = emergência).
function buildCamSignalLossFlow(): Flow {
  const nodes: Record<string, Omit<FlowNode, 'id'>> = {
    start: {
      tipo: 'inicio',
      texto:
        'As câmeras pararam de transmitir (perda de sinal). Um e-mail padrão de comunicação foi enviado ' +
        'às partes (EoR, RT, JF Brasil, TetraTech). Redobre a atenção nos instrumentos geotécnicos e ' +
        'tiltímetros enquanto as câmeras estiverem fora.',
      next: '1',
    },
    '1': {
      tipo: 'decisao',
      texto: 'Os demais instrumentos (geotécnicos e tiltímetros) continuam operando normalmente?',
      sim: '2',
      nao: '6',
      simNote: 'Câmeras são o único ponto de falha — siga para reiniciar o sistema.',
      naoNote: 'Outros equipamentos também falharam — avalie os tiltímetros imediatamente.',
    },
    '2': { tipo: 'acao', texto: 'Reinicie o sistema de câmeras (software de monitoramento TECAL).', next: '3' },
    '3': {
      tipo: 'decisao',
      texto: 'O sistema voltou ao normal?',
      sim: 'fim_ok',
      nao: '4a',
      simNote: 'Câmeras restabelecidas. Registre o ocorrido e retome a rotina.',
      naoNote: 'Sistema não restabeleceu — siga para manutenção.',
    },
    '4a': {
      tipo: 'acao',
      texto: 'Informe o RT e solicite apoio técnico de manutenção das câmeras (TECAL).',
      wa: {
        to: '',
        msg: '[GeoGuard UPM] Perda de sinal das câmeras. Sistema não restabeleceu após reinicialização. Solicito apoio de manutenção (TECAL).',
        label: 'WhatsApp RT Foscentro',
      },
      next: '4b',
    },
    '4b': {
      tipo: 'acao',
      texto:
        'Solicite uma verificação preventiva do local das estruturas pela equipe de segurança patrimonial. ' +
        'Registre o horário da solicitação.',
      next: '5',
    },
    '5': {
      tipo: 'fim',
      texto:
        'Aguarde orientação do RT e continue o acompanhamento dos níveis de controle dos instrumentos ' +
        'geotécnicos e do sistema de sirenes até o restabelecimento das câmeras. Registre o ocorrido no ' +
        'Log (M10).',
    },
    '6': {
      tipo: 'decisao',
      texto: 'Os TILTÍMETROS também perderam sinal?',
      sim: '7',
      nao: '4a',
      simNote: 'EMERGÊNCIA: câmeras E tiltímetros sem sinal. Acione o protocolo de emergência imediatamente.',
      naoNote: 'Só as câmeras falharam — siga para manutenção.',
    },
    '7': {
      tipo: 'paebm',
      texto:
        'EMERGÊNCIA — câmeras E tiltímetros com perda de sinal simultânea. Acione as sirenes imediatamente ' +
        'e comunique o RT para acionamento do Protocolo PAEBM.',
      paebm: true,
    },
    fim_ok: {
      tipo: 'fim',
      texto:
        'Sistema restabelecido. Registre o ocorrido (duração, causa identificada) no Log (M10) e retome a ' +
        'rotina de monitoramento. TARP pode ser encerrada.',
    },
  };
  // GUPM-207: sub-fluxo de emergência embutido antes do terminal `paebm` ('7').
  return spliceEmergency(
    {
      start: 'start',
      nodes: withIds(nodes),
      order: ['start', '1', '2', '3', '4a', '4b', '5', '6', '7', 'fim_ok'],
    },
    '7',
  );
}

// Perda de sinal dos tiltímetros.
function buildTiltSignalLossFlow(): Flow {
  const nodes: Record<string, Omit<FlowNode, 'id'>> = {
    start: {
      tipo: 'inicio',
      texto:
        'Os tiltímetros perderam sinal. Atenção: tiltímetros são o sensor de ruptura automático — na menor ' +
        'dúvida de ruptura em curso, acione a emergência ANTES de qualquer outra ação.',
      next: '1',
    },
    '1': {
      tipo: 'decisao',
      texto: 'É uma situação INDUVIDÁVEL de ruptura em curso?',
      sim: '7',
      nao: '2',
      simNote: 'Acione as sirenes e o Protocolo PAEBM IMEDIATAMENTE.',
      naoNote: 'Sem sinais visuais claros de ruptura — siga a verificação.',
    },
    '2': {
      tipo: 'acao',
      texto:
        'Verifique se houve acionamento automático das sirenes (AAS — Sistema de Notificação em Massa). ' +
        'Confirme nas telas do sistema.',
      next: '3',
    },
    '3': {
      tipo: 'acao',
      texto:
        'Confirme pelas câmeras que NÃO há algo anormal na estrutura (movimentação de solo, fissuras ' +
        'visíveis, assoreamento, galgamento).',
      next: '4',
    },
    '4': {
      tipo: 'acao',
      texto:
        'Se constatado FALSO ALERTA (sem anomalia visual nas câmeras): desligue o acionamento das sirenes ' +
        'pela interface do sistema (TECAL/SNM).',
      next: '5',
    },
    '5': {
      tipo: 'acao',
      texto: 'Informe o RT e solicite apoio técnico de manutenção do sistema de tiltímetros e sirenes (TECAL).',
      wa: {
        to: '',
        msg: '[GeoGuard UPM] Perda de sinal dos tiltímetros. Verificação nas câmeras não indica anomalia. Solicito manutenção (TECAL).',
        label: 'WhatsApp TECAL',
      },
      next: '6',
    },
    '6': {
      tipo: 'fim',
      texto:
        'Aguarde orientação do RT e continue o acompanhamento da estrutura pelas câmeras e pelos demais ' +
        'instrumentos geotécnicos. Registre o ocorrido no Log (M10).',
    },
    '7': {
      tipo: 'paebm',
      texto: 'EMERGÊNCIA — ruptura em curso. Acione as sirenes e comunique o RT para o Protocolo PAEBM.',
      paebm: true,
    },
  };
  // GUPM-207: sub-fluxo de emergência embutido antes do terminal `paebm` ('7').
  return spliceEmergency(
    {
      start: 'start',
      nodes: withIds(nodes),
      order: ['start', '1', '2', '3', '4', '5', '6', '7'],
    },
    '7',
  );
}

/**
 * Tiltímetro em Emergência pela Carta de Controle (TARP-TILT-EMG) — C-88.
 *
 * É o "fluxo dos tiltímetros" do protótipo, que ele abria tanto para perda de sinal quanto
 * para a condição de acionamento (`abrirFluxoTiltimetro`): mesmos nós e mesma ordem do
 * `buildTiltSignalLossFlow`, com abertura e mensagem à manutenção próprias — aqui o sensor
 * PUBLICOU um valor de Emergência, não está mudo. A Emergência da Carta coincide com o
 * gatilho do acionamento automático das sirenes do R-A04; o limite real em campo é
 * confirmação pendente com o RT, por isso o texto não cita graus.
 */
function buildTiltEmergencyFlow(): Flow {
  const flow = buildTiltSignalLossFlow();
  const n = flow.nodes;
  n['start']!.texto =
    'Tiltímetro em Emergência pela Carta de Controle. Tiltímetros são o sensor de ruptura: verifique se ' +
    'as sirenes dispararam automaticamente e, na menor dúvida de ruptura em curso, acione a emergência ' +
    'ANTES de qualquer outra ação.';
  n['5']!.texto = 'Informe o RT e solicite a verificação do tiltímetro e das sirenes à manutenção (TECAL).';
  n['5']!.wa = {
    to: '',
    msg:
      '[GeoGuard UPM] Tiltímetro em Emergência pela Carta de Controle. Verificação nas câmeras não indica ' +
      'anomalia. Solicito verificação do sensor e das sirenes (TECAL).',
    label: 'WhatsApp TECAL',
  };
  return flow;
}

/**
 * C-88 — o "(ciclo normal)" do prazo de releitura de Atenção é o ciclo de 12 h do PZ.
 * O fluxo genérico de instrumento (TARP-INST-AT) serve marco, régua, pluviômetro…, então
 * fica só o prazo.
 */
function semCicloNormal(flow: Flow): Flow {
  const passo = flow.nodes['2'];
  if (passo) passo.texto = passo.texto.replace(' (ciclo normal)', '');
  return flow;
}

// Detecção de anomalia via câmera (TARP-VIS-AL).
function buildVisualAnomalyFlow(): Flow {
  const nodes: Record<string, Omit<FlowNode, 'id'>> = {
    start: {
      tipo: 'inicio',
      texto: 'Vigilante identificou algo anormal no monitoramento por câmeras. Ponto de entrada: anomalia visual constatada.',
      next: '1',
    },
    '1': {
      tipo: 'decisao',
      texto: 'Você confirma que viu algo anormal nas câmeras?',
      sim: '3',
      nao: '2',
      simNote: 'Prossiga pelo fluxo de avaliação da anomalia.',
      naoNote: 'Se não há nada anormal, retorne ao acompanhamento de rotina.',
    },
    '2': {
      tipo: 'fim',
      // GUPM-190 T1: "nada anormal" → rotina, sem registrar TARP (só descartar/cancelar).
      semRegistro: true,
      texto: 'Continue o acompanhamento de rotina. Nada anormal confirmado — mantenha a vigilância pelas câmeras.',
    },
    '3': {
      tipo: 'decisao',
      texto:
        'É uma situação INDUVIDÁVEL de ruptura em curso? (galgamento, escorregamento, trinca abrindo, jato de ' +
        'água, colapso visível)',
      sim: '17',
      nao: '4',
      simNote: 'Acione as sirenes e o Protocolo PAEBM IMEDIATAMENTE.',
      naoNote: 'Sem sinal claro de ruptura — avalie o risco.',
    },
    '4': {
      tipo: 'decisao',
      texto: 'É uma situação de risco (possível rompimento constatado)?',
      sim: '5',
      nao: '15',
      simNote: 'Contate imediatamente o RT — cadeia de escalonamento.',
      naoNote: 'Avalie risco a pessoas, equipamento ou operação.',
    },
    '5': {
      tipo: 'acao',
      texto:
        'Contate o RT (celular e WhatsApp) e aguarde orientação. Fotografe ou registre em vídeo a anomalia. ' +
        'Não se aproxime da área afetada sem autorização.',
      wa: {
        to: '',
        msg: '[GeoGuard UPM] Anomalia visual identificada nas câmeras. Possível situação de risco. Aguardando orientação do RT.',
        label: 'WhatsApp RT Foscentro',
      },
      next: '6',
    },
    '6': { tipo: 'decisao', texto: 'O RT respondeu?', sim: '11', nao: '7' },
    '7': {
      tipo: 'acao',
      texto: 'Contate o contato secundário da cadeia de escalonamento (celular e WhatsApp).',
      next: '8',
    },
    '8': { tipo: 'decisao', texto: 'O contato secundário respondeu?', sim: '11', nao: '9' },
    '9': {
      tipo: 'acao',
      texto: 'Contate o contato terciário da cadeia de escalonamento (celular e WhatsApp).',
      next: '10',
    },
    '10': { tipo: 'decisao', texto: 'O contato terciário respondeu?', sim: '11', nao: '12' },
    '11': {
      tipo: 'decisao',
      texto: 'Após contato com o responsável: é uma situação de ruptura?',
      sim: '17',
      nao: '16a',
      naoNote: 'Sem ruptura — encaminhar para baixa da TARP pelo técnico.',
    },
    '12': {
      tipo: 'acao',
      texto:
        'Nenhum contato da cadeia respondeu. Aguarde e acompanhe a situação pelas câmeras. Documente o que observar ' +
        '(horário, evolução da anomalia).',
      next: '13',
    },
    '13': { tipo: 'decisao', texto: 'A situação está progredindo?', sim: '14', nao: '12' },
    '14': {
      tipo: 'decisao',
      texto: 'A ruptura está prestes a acontecer ou está acontecendo (caso induvidável)?',
      sim: '17',
      nao: '5',
    },
    '15': {
      tipo: 'decisao',
      texto: 'A situação coloca em risco pessoas, equipamento ou operação?',
      // GUPM-207 (achado do dono): "risco a pessoas" segue para a cadeia de escalonamento
      // (nó 5 — contatar o RT), não volta ao nó 4. O roteamento antigo (sim→'4') criava
      // um laço 4↔15 quando 4=Não (nenhum terminal alcançável). Desvio deliberado do V8.
      sim: '5',
      nao: '16a',
      simNote: 'Ligue para o RT e isole a área — segue para a cadeia de escalonamento.',
      naoNote: 'Reportar ao setor responsável e encaminhar para baixa.',
    },
    '16a': {
      tipo: 'baixa',
      texto:
        'Encaminhe para a baixa da TARP. O preenchimento do formulário — incluindo enquadramento ANM e ' +
        'plano de ação — é realizado pelo técnico.',
      handoff: true,
    },
    '17': {
      tipo: 'paebm',
      texto: 'EMERGÊNCIA — ruptura em curso ou induvidável. Acione as sirenes e comunique o RT para o Protocolo PAEBM.',
      paebm: true,
    },
  };
  // GUPM-207: sub-fluxo de emergência embutido antes do terminal `paebm` ('17').
  return spliceEmergency(
    {
      start: 'start',
      nodes: withIds(nodes),
      order: ['start', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16a', '17'],
    },
    '17',
  );
}

// ── Catálogo: 9 TARPs do v5.10 + 3 do C-88 ────────────────────
export const TARP_CATALOG: Record<TarpCode, TarpCatalogEntry> = {
  'TARP-PZ-AT': {
    code: 'TARP-PZ-AT',
    nivel: 'ATENCAO',
    requerDiagnostico: true,
    title: "⚠️ ATENÇÃO — Nível d'água acima do normal",
    subtitle: 'Instrumento ultrapassou o limite normal. Consulta técnica obrigatória antes de qualquer acionamento.',
    steps: [
      'Verifique as câmeras. Há algo incomum no talude de jusante (umidade, surgências)?',
      'Anote suas observações no campo de observação abaixo.',
      'LIGUE AGORA para o RT Foscentro (contato abaixo).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] ATENÇÃO — Instrumento fora do normal. Validar TARP. Via 3 — Instrumentação.',
        label: '📱 WhatsApp RT Foscentro — envie foto e descrição da anomalia.',
      },
      'Informe: hora da leitura, valor, nome do instrumento e o que viu nas câmeras.',
      'Aguarde parecer do RT Foscentro + RT JF Brasil. Não tome ações adicionais sem autorização.',
      'Continue as leituras normais dos demais instrumentos.',
      'Registre o parecer e solicite ao técnico responsável a baixa desta TARP.',
    ],
    warning: '⚠️ Não conclua que o instrumento está danificado. Trate como anomalia real até o RT confirmar.',
    flow: buildInstFlow('ATENCAO'),
  },
  'TARP-PZ-AL': {
    code: 'TARP-PZ-AL',
    nivel: 'ALERTA',
    requerDiagnostico: true,
    title: "🚨 ALERTA — Nível d'água em patamar crítico",
    subtitle: 'Piezômetro/INA ultrapassou o limite de Alerta. Consulta técnica obrigatória imediata.',
    steps: [
      'Verifique as câmeras. Há surgência, umidade ou carreamento no talude de jusante?',
      'LIGUE IMEDIATAMENTE para o RT Foscentro (contato abaixo).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] ALERTA — Nível d\'água em patamar crítico. Via 3 — Instrumentação. Aguardando validação de TARP.',
        label: '📱 WhatsApp RT Foscentro — envie valor da leitura e fotos.',
      },
      'Repita a leitura do instrumento em até 2 horas e registre o novo valor.',
      'Anote suas observações no campo abaixo (hora, valor, instrumento, câmeras).',
      'Aguarde o parecer do RT Foscentro + RT JF Brasil. Não tome ações adicionais sem autorização.',
      'Continue as leituras normais dos demais instrumentos.',
      'Registre o parecer e solicite ao técnico responsável a baixa desta TARP.',
    ],
    warning: '⚠️ Nível de Alerta: releitura em até 2h. Trate como anomalia real até o RT concluir o diagnóstico.',
    flow: buildInstFlow('ALERTA'),
  },
  'TARP-MS-AL': {
    code: 'TARP-MS-AL',
    nivel: 'ALERTA',
    requerDiagnostico: true,
    title: '🚨 ALERTA — Deslocamento crítico detectado',
    subtitle: 'Marco superficial ultrapassou limite de Alerta. Consulta técnica obrigatória imediata.',
    steps: [
      'LIGUE IMEDIATAMENTE para o RT Foscentro (contato abaixo).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] ALERTA — Deslocamento crítico. Via 3 — Instrumentação. Aguardando validação de TARP.',
        label: '📱 WhatsApp RT Foscentro — envie valor da leitura e fotos.',
      },
      'Não permita acesso à área da barragem. Isole o perímetro.',
      'Registre o estado atual pelas câmeras — print ou vídeo.',
      'Repita a leitura do instrumento e registre o novo valor.',
      'Aguarde instruções do RT. Prepare-se para acionar protocolo de emergência.',
      'Solicite baixa desta TARP ao RT Foscentro após receber o parecer oficial.',
    ],
    warning: '⚠️ Marco em prioridade máxima (EoR). Trate qualquer leitura anormal como emergência real.',
    flow: buildInstFlow('ALERTA'),
  },
  'TARP-VIS-AL': {
    code: 'TARP-VIS-AL',
    nivel: 'ALERTA',
    title: '🚨 ALERTA — Anomalia visual identificada',
    subtitle: 'Anomalia em câmera ou inspeção de campo.',
    steps: [
      'Fotografe ou registre em vídeo a anomalia identificada.',
      'Anote a localização aproximada (crista, berma, talude, pé...).',
      'LIGUE para o RT Foscentro (contato abaixo).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] ALERTA VISUAL — Anomalia identificada. Foto em anexo. Via 2 — Visual. Aguardando orientação.',
        label: '📱 WhatsApp RT Foscentro — envie foto/vídeo da anomalia.',
      },
      'Não se aproxime da área afetada sem autorização.',
      'Aguarde orientação antes de qualquer ação adicional.',
      'Somente encerre esta TARP após avaliação e parecer do RT responsável.',
    ],
    warning: '⚠️ Surgência no talude pode indicar piping — modo de falha crítico. Nunca subestime.',
    flow: buildVisualAnomalyFlow(),
  },
  'TARP-EMG': {
    code: 'TARP-EMG',
    nivel: 'EMERGENCIA',
    title: '🆘 EMERGÊNCIA — Situação de Ruptura em Curso',
    subtitle: 'Situação induvidável de ruptura. Acione as sirenes e a cadeia de emergência agora.',
    steps: [
      'Confirme: a ruptura está prestes a acontecer ou está acontecendo (caso induvidável)?',
      'ACIONE AS SIRENES da ZAS (UPM-01 a UPM-06) — sistema externo Tecal.',
      'LIGUE AGORA para o RT Foscentro (contato abaixo) — para acionamento do Coordenador do PAEBM.',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] 🆘 EMERGÊNCIA — Ruptura em curso. Sirenes acionadas. Acionar Coordenador do PAEBM.',
        label: '📱 WhatsApp de emergência — envie situação completa.',
      },
      'Comunique: nome da barragem, situação observada e que as sirenes foram acionadas.',
      'Notifique a Defesa Civil (SEDEC) — contato abaixo.',
      'Apoie a Defesa Civil na evacuação preventiva/imediata da ZAS.',
      'Restrinja o acesso à barragem e ao vale de jusante. Não abandone o posto.',
      'Registre todos os contatos realizados com horários no log.',
    ],
    warning: '🆘 RUPTURA EM CURSO. Acione as sirenes imediatamente. Cada contato deve ser registrado com horário.',
    flow: buildEmgFlow(),
  },
  'TARP-PS-INST': {
    code: 'TARP-PS-INST',
    nivel: 'ATENCAO',
    requerDiagnostico: true,
    perdaSinal: true,
    title: '📡 PERDA DE SINAL — Instrumento geotécnico',
    subtitle: 'Um instrumento parou de enviar leituras. Não é leitura de nível — é falha de sinal. Trate com calma, seguindo os passos.',
    steps: [
      'Confirme nas câmeras: há algo incomum na estrutura perto do instrumento? (Se SIM, isto vira anomalia visual.)',
      'Anote qual instrumento perdeu o sinal e o horário em que parou de atualizar.',
      'Solicite a verificação da integridade do instrumento à TetraTech (manutenção). Prazo: até 48 horas.',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] PERDA DE SINAL de instrumento. Solicito verificação de integridade (TetraTech).',
        label: '📱 Acionar TetraTech (manutenção da instrumentação).',
      },
      'Comunique o RT Foscentro (contato abaixo).',
      'Continue acompanhando a estrutura pelas câmeras e pelos demais instrumentos.',
      'Aguarde: se houver avaria, o RT direciona para baixa. Se não, o RT fará diagnóstico técnico (até 48h).',
    ],
    warning: '⚠️ Perda de sinal NÃO significa emergência. Não gera nível da estrutura. E-mail automático para EoR, RT, JF Brasil e TetraTech.',
    flow: buildSignalLossFlow(),
  },
  'TARP-PS-CAM': {
    code: 'TARP-PS-CAM',
    nivel: 'ATENCAO',
    perdaSinal: true,
    title: '📹 PERDA DE SINAL — Câmeras',
    subtitle: 'As câmeras pararam de transmitir. Siga os passos para restabelecer e garantir a vigilância enquanto isso.',
    steps: [
      'Os demais instrumentos continuam operando? (Se NÃO e os tiltímetros também caíram → emergência.)',
      'Reinicie o sistema de câmeras.',
      'O sistema voltou ao normal? Se SIM, registre e retome a rotina — TARP pode ser encerrada.',
      'Se NÃO voltou: informe o RT e solicite apoio técnico de manutenção das câmeras (TECAL).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] PERDA DE SINAL das câmeras. Reinício sem sucesso. Solicito manutenção (TECAL).',
        label: '📱 Acionar TECAL (manutenção de câmeras).',
      },
      'Solicite uma verificação preventiva do local à equipe de segurança patrimonial.',
      'Aguarde orientação do RT e mantenha o acompanhamento dos níveis dos instrumentos e do sistema de sirenes.',
    ],
    warning:
      '⚠️ Sem câmeras, redobre a atenção aos instrumentos e tiltímetros. Se as câmeras E os tiltímetros ' +
      'perderem sinal juntos, acione o protocolo de emergência.',
    flow: buildCamSignalLossFlow(),
  },
  'TARP-PS-TILT': {
    code: 'TARP-PS-TILT',
    nivel: 'ALERTA',
    perdaSinal: true,
    title: '⚡ PERDA DE SINAL — Tiltímetros',
    subtitle: 'Os tiltímetros são o sensor de ruptura. A perda de sinal deles exige verificação imediata.',
    steps: [
      'É uma situação induvidável de ruptura em curso? Se SIM → acione as sirenes e a emergência AGORA.',
      'Verifique se houve acionamento automático das sirenes (AAS).',
      'Confirme pelas câmeras que NÃO há nada anormal na estrutura.',
      'Se for falso alerta (nada anormal): desligue o acionamento das sirenes.',
      'Informe o RT e solicite apoio técnico de manutenção (TECAL) dos tiltímetros/sirenes.',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] PERDA DE SINAL dos tiltímetros. Verificação em curso.',
        label: '📱 Acionar TECAL (tiltímetros / sirenes).',
      },
      'Aguarde orientação do RT e mantenha o acompanhamento da estrutura pelas câmeras.',
    ],
    warning: '⚡ Tiltímetro é o gatilho de ruptura. Na menor dúvida de ruptura em curso, acione emergência antes de qualquer outra coisa.',
    flow: buildTiltSignalLossFlow(),
  },
  'TARP-INST-EMG': {
    code: 'TARP-INST-EMG',
    nivel: 'EMERGENCIA',
    requerDiagnostico: true,
    title: '🆘 EMERGÊNCIA — Instrumento em nível de emergência',
    subtitle: 'Instrumento em Emergência (ou vários na mesma seção/barragem). Releitura em até 1h. Siga o fluxo.',
    steps: [
      'Solicite a releitura do instrumento em até 1 hora.',
      'Comunique o RT imediatamente (celular/WhatsApp).',
      'Acompanhe pelas câmeras e aguarde diagnóstico do RT (até 12h).',
    ],
    warning: '🆘 Nível de Emergência de instrumentação. Releitura em 1h e RT comunicado imediatamente por celular/WhatsApp.',
    flow: buildInstFlow('EMERGENCIA'),
  },
  // ── C-88 (ADR-0038) ─────────────────────────────────────────
  // O fluxo genérico de instrumento é o mesmo de PZ-AT/PZ-AL (o protótipo já o aplicava a
  // qualquer tipo no `tarpFor`); muda o código e o título, que diziam "nível d'água".
  'TARP-INST-AT': {
    code: 'TARP-INST-AT',
    nivel: 'ATENCAO',
    requerDiagnostico: true,
    title: '⚠️ ATENÇÃO — Instrumento acima do normal',
    subtitle:
      'Instrumento ultrapassou o limite de Atenção da Carta de Controle. Consulta técnica obrigatória antes de qualquer acionamento.',
    steps: [
      'Verifique as câmeras. Há algo incomum na estrutura?',
      'Anote suas observações no campo de observação abaixo.',
      'LIGUE AGORA para o RT Foscentro (contato abaixo).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] ATENÇÃO — Instrumento fora do normal. Validar TARP. Via 3 — Instrumentação.',
        label: '📱 WhatsApp RT Foscentro — envie foto e descrição da anomalia.',
      },
      'Informe: hora da leitura, valor, nome do instrumento e o que viu nas câmeras.',
      'Aguarde parecer do RT Foscentro + RT JF Brasil. Não tome ações adicionais sem autorização.',
      'Registre o parecer e solicite ao técnico responsável a baixa desta TARP.',
    ],
    warning: '⚠️ Não conclua que o instrumento está danificado. Trate como anomalia real até o RT confirmar.',
    flow: semCicloNormal(buildInstFlow('ATENCAO')),
  },
  'TARP-INST-AL': {
    code: 'TARP-INST-AL',
    nivel: 'ALERTA',
    requerDiagnostico: true,
    title: '🚨 ALERTA — Instrumento em patamar crítico',
    subtitle: 'Instrumento ultrapassou o limite de Alerta da Carta de Controle. Consulta técnica obrigatória imediata.',
    steps: [
      'Verifique as câmeras. Há algo incomum na estrutura?',
      'LIGUE IMEDIATAMENTE para o RT Foscentro (contato abaixo).',
      {
        type: 'whatsapp',
        to: '',
        msg: '[GeoGuard UPM] ALERTA — Instrumento em patamar crítico. Via 3 — Instrumentação. Aguardando validação de TARP.',
        label: '📱 WhatsApp RT Foscentro — envie valor da leitura e fotos.',
      },
      'Confirme a leitura do instrumento em até 2 horas e registre o novo valor.',
      'Aguarde o parecer do RT Foscentro + RT JF Brasil. Não tome ações adicionais sem autorização.',
      'Registre o parecer e solicite ao técnico responsável a baixa desta TARP.',
    ],
    warning: '⚠️ Nível de Alerta: confirme a leitura em até 2h. Trate como anomalia real até o RT concluir o diagnóstico.',
    flow: buildInstFlow('ALERTA'),
  },
  'TARP-TILT-EMG': {
    code: 'TARP-TILT-EMG',
    nivel: 'EMERGENCIA',
    title: '🆘 EMERGÊNCIA — Tiltímetro em nível de emergência',
    subtitle:
      'Tiltímetro (sensor de ruptura) ultrapassou o limite de Emergência da Carta de Controle. Siga o fluxo dos tiltímetros.',
    steps: [
      'É uma situação induvidável de ruptura em curso? Se SIM → acione as sirenes e a emergência AGORA.',
      'Verifique se houve acionamento automático das sirenes (AAS).',
      'Confirme pelas câmeras que NÃO há nada anormal na estrutura.',
      'Se for falso alerta (nada anormal): desligue o acionamento das sirenes.',
      'Informe o RT e solicite a verificação do tiltímetro e das sirenes (TECAL).',
      'Aguarde orientação do RT e mantenha o acompanhamento da estrutura pelas câmeras.',
    ],
    warning: '⚡ Tiltímetro é o gatilho de ruptura. Na menor dúvida de ruptura em curso, acione emergência antes de qualquer outra coisa.',
    flow: buildTiltEmergencyFlow(),
  },
};

export const TARP_CODES = Object.keys(TARP_CATALOG) as TarpCode[];

/** Type guard: `v` é um código de TARP conhecido do catálogo v5.10 (GUPM-185, review #101). */
export function isTarpCode(v: string): v is TarpCode {
  return (TARP_CODES as readonly string[]).includes(v);
}

// ── Gatilho → TARP ────────────────────────────────────────────
export type TarpTrigger =
  | { kind: 'instrumento'; level: TarpLevel; instrumentType?: string }
  | { kind: 'visual' }
  | { kind: 'perda-sinal'; source: 'instrumento' | 'cameras' | 'tiltimetros'; tiltimetrosTambemCairam?: boolean }
  | { kind: 'emergencia' };

const MARCO_TYPES: ReadonlySet<string> = new Set(['MS', 'MSE']);
/** Tipos que medem nível d'água — os únicos que recebem PZ-AT/PZ-AL ("nível d'água"). */
const NIVEL_DAGUA_TYPES: ReadonlySet<string> = new Set(['PZ', 'INA']);

/**
 * Resolve qual TARP abrir para um gatilho. Codifica a regra headline v5.10:
 * câmeras + tiltímetros sem sinal simultâneo = emergência.
 *
 * Gatilho de instrumento — C-88 (decisão do dono, 14/10/2026; ADR-0038):
 * - Emergência é sempre Emergência: tiltímetro → TILT-EMG (fluxo dos tiltímetros); os
 *   demais → INST-EMG. No protótipo a regra do marco vinha antes e o marco em Emergência
 *   abria a TARP de Alerta.
 * - Atenção/Alerta: PZ e INA → PZ-AT/PZ-AL; marco → INST-AT (Atenção) / MS-AL (Alerta);
 *   os demais → INST-AT/INST-AL. O default deixou de ser o protocolo do piezômetro — era
 *   esse default que obrigava o atalho a barrar o tiltímetro e o clique caía em silêncio.
 * - Sem tipo informado mantém o comportamento anterior (PZ-AT/PZ-AL).
 */
export function tarpForTrigger(trigger: TarpTrigger): TarpCode {
  switch (trigger.kind) {
    case 'emergencia':
      return 'TARP-EMG';
    case 'visual':
      return 'TARP-VIS-AL';
    case 'instrumento': {
      const tipo = trigger.instrumentType;
      if (trigger.level === 'EMERGENCIA') return tipo === 'TILT' ? 'TARP-TILT-EMG' : 'TARP-INST-EMG';
      const alerta = trigger.level === 'ALERTA';
      if (tipo && MARCO_TYPES.has(tipo)) return alerta ? 'TARP-MS-AL' : 'TARP-INST-AT';
      if (tipo === undefined || NIVEL_DAGUA_TYPES.has(tipo)) return alerta ? 'TARP-PZ-AL' : 'TARP-PZ-AT';
      return alerta ? 'TARP-INST-AL' : 'TARP-INST-AT';
    }
    case 'perda-sinal':
      if (trigger.source === 'instrumento') return 'TARP-PS-INST';
      if (trigger.source === 'tiltimetros') return 'TARP-PS-TILT';
      // câmeras: se os tiltímetros também caíram → emergência
      return trigger.tiltimetrosTambemCairam ? 'TARP-EMG' : 'TARP-PS-CAM';
  }
}

export function tarpEntry(code: string): TarpCatalogEntry | null {
  return (TARP_CATALOG as Record<string, TarpCatalogEntry>)[code] ?? null;
}

/** Protocolos genéricos de instrumento: título e subtítulo dizem "Instrumento" e aceitam o tipo. */
const INST_GENERICOS: ReadonlySet<string> = new Set(['TARP-INST-AT', 'TARP-INST-AL', 'TARP-INST-EMG']);

/** O protocolo é genérico de instrumento (o rótulo pode nomear o tipo)? — C-88. */
export function ehProtocoloGenericoDeInstrumento(code: string): boolean {
  return INST_GENERICOS.has(code);
}

/** Nome legível do tipo (`INSTRUMENT_TYPE_META`), ou `null` se o tipo for desconhecido. */
export function nomeDoTipoDeInstrumento(tipo: string | null | undefined): string | null {
  if (!tipo) return null;
  return INSTRUMENT_TYPE_META[tipo as InstrumentType]?.label ?? null;
}

/** Troca a palavra "Instrumento" pelo tipo nos protocolos genéricos; demais textos intactos. */
function comTipo(code: string, texto: string, instrumentType?: string | null): string {
  const nome = INST_GENERICOS.has(code) ? nomeDoTipoDeInstrumento(instrumentType) : null;
  return nome ? texto.replace('Instrumento', nome) : texto;
}

/**
 * Título da TARP com o tipo do instrumento — C-88 (decisão D3: o código diz o protocolo,
 * o título e a lista dizem o tipo, como o protótipo faz com "TARP-MS-AL · MSE-03").
 * Só os protocolos genéricos de instrumento mudam: "Instrumento" vira o nome do tipo — o
 * teste `titulo-e-rotulo-mostram-o-tipo` trava a palavra nos títulos do catálogo.
 * Código desconhecido devolve `null`; tipo ausente/desconhecido, o título do catálogo.
 */
export function tituloDaTarp(code: string, instrumentType?: string | null): string | null {
  const entry = tarpEntry(code);
  return entry ? comTipo(code, entry.title, instrumentType) : null;
}

/** Subtítulo da TARP com o tipo do instrumento — mesma regra de `tituloDaTarp`. */
export function subtituloDaTarp(code: string, instrumentType?: string | null): string | null {
  const entry = tarpEntry(code);
  return entry ? comTipo(code, entry.subtitle, instrumentType) : null;
}

/** Protocolos genéricos de Atenção/Alerta cujo passo de releitura muda no tiltímetro. */
const RELEITURA_ADAPTAVEL: ReadonlySet<string> = new Set(['TARP-INST-AT', 'TARP-INST-AL']);

/**
 * Fluxo da TARP para o tipo do instrumento — C-88 (decisão D5).
 *
 * O tiltímetro publica sozinho a cada ~30 min: "solicite a repetição da leitura" não é
 * algo que o operador consiga fazer. Para ele, o passo 2 do fluxo genérico vira "confira a
 * próxima leitura publicada", mantendo o prazo. Só o texto muda — ids e ordem dos nós são
 * os mesmos, então um rascunho salvo continua retomável. Devolve uma CÓPIA: o catálogo é
 * compartilhado e não pode ser mutado por quem renderiza.
 */
export function fluxoDaTarp(code: string, instrumentType?: string | null): Flow | null {
  const flow = tarpEntry(code)?.flow ?? null;
  if (!flow || instrumentType !== 'TILT' || !RELEITURA_ADAPTAVEL.has(code)) return flow;
  const passo = flow.nodes['2'];
  if (!passo) return flow;
  // "Ciclo normal de 12 h" é a cadência do PZ, não a do tiltímetro — fica só o prazo.
  const prazo = /Prazo para a releitura: (.+)$/.exec(passo.texto)?.[1]?.replace(' (ciclo normal)', '');
  const texto =
    'Confira a próxima leitura publicada pelo tiltímetro (ele publica a cada cerca de 30 min) e acompanhe ' +
    'pelas câmeras.' + (prazo ? ` Prazo para a confirmação: ${prazo}` : '');
  return { ...flow, nodes: { ...flow.nodes, '2': { ...passo, texto } } };
}

/**
 * Total de passos do protocolo de um TARP = tamanho da ordem canônica do fluxo
 * (GUPM-190 T6). Alimenta o Stepper da lista quando `steps_total` chega null do
 * banco (TARPs em aberto). Retorna 0 para código desconhecido ou sem fluxo.
 */
export function flowStepsTotal(code: string): number {
  const flow = tarpEntry(code)?.flow;
  return flow ? orderedNodeIds(flow).length : 0;
}

export interface FlowSteps {
  done: number;
  total: number;
}

/**
 * Progresso do passo a passo de um TARP para o Stepper da lista (GUPM-190 T6):
 * `total` = override do banco (`steps_total`) quando presente, senão derivado do
 * fluxo (`flowStepsTotal`); `done` = passos concluídos, **capado** a `[0, total]`.
 * O cap protege contra desalinhamento quando o fluxo encolhe (ex.: `buildEmgFlow`
 * caiu de 9→6 nós) e um registro antigo tem `stepsCompleted > total` novo.
 */
export function flowStepsProgress(
  code: string,
  stepsCompleted: number,
  stepsTotalOverride?: number | null,
): FlowSteps {
  const total = stepsTotalOverride ?? flowStepsTotal(code);
  return { total, done: Math.max(0, Math.min(stepsCompleted, total)) };
}
